import {
  buildAdminQueryRoot,
  buildScopedAdminQueryKey,
  type AdminQueryKey,
  type AdminQueryRoot,
  type OrganizationIdInput,
} from '@/lib/organization-scoping'
import { queryOptions, useQuery, type QueryFunctionContext } from '@tanstack/react-query'
import { useRouteOrganizationId } from '@/hooks/useRouteOrganizationId'
import { getCheckoutAttemptStatus, type CheckoutAttemptStatusRequest } from './checkout.api'

interface OrganizationScopeInput {
  organizationId?: OrganizationIdInput
}

type StatusQueryInput = OrganizationScopeInput &
  Omit<CheckoutAttemptStatusRequest, 'organizationId'>
type StatusQueryKey = [AdminQueryRoot & CheckoutAttemptStatusRequest & { organizationId: number }]
type QueryParams<TOptions> = Partial<Omit<TOptions, 'queryKey' | 'queryFn'>>

function runCheckoutAttemptStatusQuery({
  queryKey: [{ organizationId, attemptId }],
}: Pick<QueryFunctionContext<StatusQueryKey>, 'queryKey'>) {
  return getCheckoutAttemptStatus({ organizationId, attemptId })
}

export const checkoutQueryKeys = {
  all: ({ organizationId }: OrganizationScopeInput = {}): AdminQueryKey => [
    buildAdminQueryRoot({ entity: 'checkout' }, { organizationId }),
  ],
  attemptStatus: ({ organizationId, attemptId }: StatusQueryInput = {}): StatusQueryKey =>
    buildScopedAdminQueryKey(checkoutQueryKeys.all({ organizationId })[0], {
      organizationId,
      scope: 'attemptStatus',
      attemptId,
    }),
}

export const checkoutQueries = {
  attemptStatus: ({ organizationId, attemptId }: StatusQueryInput = {}) =>
    queryOptions({
      queryKey: checkoutQueryKeys.attemptStatus({ organizationId, attemptId }),
      queryFn: runCheckoutAttemptStatusQuery,
      enabled: Boolean(attemptId),
      staleTime: 0,
    }),
}

export function useCheckoutAttemptStatus(
  attemptId: CheckoutAttemptStatusRequest['attemptId'],
  options: QueryParams<ReturnType<typeof checkoutQueries.attemptStatus>> = {}
) {
  const organizationId = useRouteOrganizationId()

  return useQuery({
    ...checkoutQueries.attemptStatus({ organizationId, attemptId }),
    ...options,
  })
}
